import React, { useState, useEffect } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import './ClubCoordinator.css';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const MemberApplications = ({ applications, loading, showTitle = true }) => {
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editApprover, setEditApprover] = useState(false);
  const [approverData, setApproverData] = useState({
    fmlcApprTitleFullNames: '',
    fmlcApprPosition: 'FMLC Coordinator',
    fmlcApprDate: '',
    fmlcApprSignature: ''
  });

  useEffect(() => {
    setFilteredApplications(
      applications.filter(app =>
        app.fullNames?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.personalEmailAddress?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [searchTerm, applications]);

  const viewApplicationDetails = (application) => {
    setSelectedApplication(application);
    setApproverData({
      fmlcApprTitleFullNames: application.fmlcApprTitleFullNames || '',
      fmlcApprPosition: application.fmlcApprPosition || 'FMLC Coordinator',
      fmlcApprDate: application.fmlcApprDate || '',
      fmlcApprSignature: application.fmlcApprSignature || ''
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedApplication(null);
    setEditApprover(false);
  };

  const handleApproverChange = (e) => {
    const { name, value } = e.target;
    setApproverData(prev => ({ ...prev, [name]: value }));
  };

  const saveApproverChanges = async () => {
    try {
      const docRef = doc(db, 'memberApplications', selectedApplication.id);
      await updateDoc(docRef, approverData);
      
      // Update local state
      const updatedApplications = applications.map(app => 
        app.id === selectedApplication.id ? { ...app, ...approverData } : app
      );
      
      setFilteredApplications(updatedApplications);
      setSelectedApplication(prev => ({ ...prev, ...approverData }));
      setEditApprover(false);
      alert('Approver details updated successfully.');
    } catch (error) {
      console.error('Error updating approver details:', error);
      alert('Failed to update approver details.');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const generateMembershipPDF = (application) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 10;
    let y = margin;
    const gold = [255, 215, 0];

    const addHeader = () => {
      y = margin;
      doc.setFillColor(...gold);
      doc.rect(margin, y, pageWidth - 2 * margin, 10, 'F');
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 0, 0);
      doc.text('FINITE MAGAZINE LIFESTYLE CLUB MEMBERSHIP FORM', pageWidth / 2, y + 7, { align: 'center' });
      y += 15;
    };

    const addFooter = () => {
      doc.setTextColor(...gold);
      doc.setFontSize(10);
      doc.text('From Lifestyle Events to Business Occasions!', pageWidth / 2, pageHeight - margin, { align: 'center' });
      doc.setTextColor(0, 0, 0);
    };

    const checkPage = (needed = 20) => {
      if (y + needed > pageHeight - margin * 2) {
        addFooter();
        doc.addPage();
        addHeader();
        return true;
      }
      return false;
    };

    addHeader();

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text('Application Type', margin, y);
    y += 6;
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text('Application Type: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.applicationType || 'N/A', margin + 60, y);
    y += 10;
    checkPage();

    doc.setFont("helvetica", "bold");
    doc.text('Membership Plan', margin, y);
    y += 6;
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text('Membership Plan: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.membershipPlan || 'N/A', margin + 60, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Member Card Fee: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.memberCardFee ? 'Yes' : 'No', margin + 60, y);
    y += 10;
    checkPage();

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text('Section 1: Personal Details', margin, y);
    y += 6;
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text('Full Names: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.fullNames || 'N/A', margin + 60, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Other Names: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.otherNames || 'N/A', margin + 60, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Date of Birth: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(formatDate(application.dateOfBirth) || 'N/A', margin + 60, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Identity Document Number: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.identityDocumentNumber || 'N/A', margin + 60, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Gender: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.gender || 'N/A', margin + 60, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Nationality: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.nationality || 'N/A', margin + 60, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Current Marital Status: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.currentMaritalStatus || 'N/A', margin + 60, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Full Names of Spouse: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.fullNamesSpouse || 'N/A', margin + 60, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Previous Marital Status: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.previousMaritalStatus?.join(', ') || 'N/A', margin + 60, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Full Names of Previous Spouse: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.fullNamesPreviousSpouse || 'N/A', margin + 60, y);
    y += 10;
    checkPage();

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text('Section 2: Contacts & Residence', margin, y);
    y += 6;
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text('Contact Numbers for Calls (Main): ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.contactNumbersCallsMain || 'N/A', margin + 80, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Contact Numbers for Calls (Other): ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.contactNumbersCallsOther || 'N/A', margin + 80, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Contact Numbers for WhatsApp/SMS (Main): ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.contactNumbersWhatsappMain || 'N/A', margin + 90, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Contact Numbers for WhatsApp/SMS (Other): ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.contactNumbersWhatsappOther || 'N/A', margin + 90, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Personal Email Address: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.personalEmailAddress || 'N/A', margin + 90, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Home Location/Village name: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.homeLocationVillage || 'N/A', margin + 90, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Street full description: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.streetFullDescription || 'N/A', margin + 90, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Name of homeowner / Chief: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.nameHomeownerChief || 'N/A', margin + 90, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Social Media Channel & Names (Link): ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.socialMediaChannelNames || 'N/A', margin + 90, y);
    y += 10;
    checkPage();

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text('Section 3: Next of Kin Details', margin, y);
    y += 6;
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text('Title & Full Names: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.nextKinTitleFullNames || 'N/A', margin + 60, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Relationship: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.nextKinRelationship || 'N/A', margin + 60, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Home Location/Village name: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.nextKinHomeLocationVillage || 'N/A', margin + 60, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Contact Number Calls: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.nextKinContactCalls || 'N/A', margin + 60, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Contact Number Text: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.nextKinContactText || 'N/A', margin + 60, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Email Address: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.nextKinEmailAddress || 'N/A', margin + 60, y);
    y += 10;
    checkPage();

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text('Section 4: Career & Employment', margin, y);
    y += 6;
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text('Current Employer / Entity name: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.currentEmployerName || 'N/A', margin + 80, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Current Start Date - End Date: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.currentStartDateEndDate || 'N/A', margin + 80, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Current Department: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.currentDepartment || 'N/A', margin + 80, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Current Position: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.currentPosition || 'N/A', margin + 80, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Current Employment Number: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.currentEmploymentNumber || 'N/A', margin + 80, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Current Contact Number Main: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.currentContactMain || 'N/A', margin + 80, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Current Place / location: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.currentPlaceLocation || 'N/A', margin + 80, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Current Industry or sector: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.currentIndustrySector || 'N/A', margin + 80, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Immediate Past Employer / Entity name: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.immediatePastEmployerName || 'N/A', margin + 80, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Immediate Past Start Date - End Date: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.immediatePastStartDateEndDate || 'N/A', margin + 80, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Immediate Past Department: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.immediatePastDepartment || 'N/A', margin + 80, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Immediate Past Position: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.immediatePastPosition || 'N/A', margin + 80, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Immediate Past Employment Number: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.immediatePastEmploymentNumber || 'N/A', margin + 80, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Immediate Past Contact Number Main: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.immediatePastContactMain || 'N/A', margin + 80, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Immediate Past Place / location: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.immediatePastPlaceLocation || 'N/A', margin + 80, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Immediate Past Industry or sector: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.immediatePastIndustrySector || 'N/A', margin + 80, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Highest Qualification: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.highestQualification || 'N/A', margin + 80, y);
    y += 10;
    checkPage();

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text('Section 7: Funeral Cover & Benefits', margin, y);
    y += 6;
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text('Finite Magazine Lifestyle Club membership includes FREE Funeral Cover (Underwritten by Liberty Life Lesotho) for all Members immediate families (T&C apply).', margin, y, { maxWidth: pageWidth - 2 * margin });
    y += 15;
    autoTable(doc, {
      startY: y,
      head: [['Membership Plan', 'Member', 'Spouse', 'Child 14-21', 'Child 6-13', 'Child 1-5', 'Child <1']],
      body: [
        ['Standard', 'M20,000', 'M20,000', 'M20,000', 'M10,000', 'M5,000', 'M2,500'],
        ['Silver', 'M30,000', 'M30,000', 'M30,000', 'M15,000', 'M7,500', 'M3,750'],
        ['Gold', 'M40,000', 'M40,000', 'M40,000', 'M20,000', 'M10,000', 'M5,000'],
        ['Platinum', 'M50,000', 'M50,000', 'M50,000', 'M25,000', 'M12,500', 'M6,250'],
      ],
      theme: 'grid',
      headStyles: { fillColor: gold },
      styles: { cellPadding: 3, fontSize: 10, overflow: 'linebreak', lineColor: [200, 200, 200], lineWidth: 0.1 },
      margin: { left: margin },
    });
    y = doc.lastAutoTable.finalY + 5;
    doc.text('IMMEDIATE DOUBLE COVER BENEFIT IN CASE OF ACCIDENTAL DEATH FOR MAIN MEMBER AND SPOUSE, NO WAITING PERIOD.', margin, y);
    y += 10;
    doc.setFont("helvetica", "bold");
    doc.text('Children', margin, y);
    y += 6;
    application.children?.forEach((child, index) => {
      checkPage();
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text(`Child ${index + 1} Name: `, margin, y);
      doc.setFont("helvetica", "normal");
      doc.text(child.name || 'N/A', margin + 60, y);
      y += 5;
      doc.setFont("helvetica", "bold");
      doc.text('Gender: ', margin, y);
      doc.setFont("helvetica", "normal");
      doc.text(child.gender || 'N/A', margin + 60, y);
      y += 5;
      doc.setFont("helvetica", "bold");
      doc.text('Date of Birth: ', margin, y);
      doc.setFont("helvetica", "normal");
      doc.text(formatDate(child.dob) || 'N/A', margin + 60, y);
      y += 5;
      doc.setFont("helvetica", "bold");
      doc.text('Issue Card: ', margin, y);
      doc.setFont("helvetica", "normal");
      doc.text(child.issueCard || 'N/A', margin + 60, y);
      y += 5;
    });
    doc.setFont("helvetica", "bold");
    doc.text('Children\'s Personalities: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.childrenPersonalities?.join(', ') || 'N/A', margin + 80, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Add Parents/In-Laws/Extended: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.addParentsInLawsExtended?.join(', ') || 'N/A', margin + 80, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Parents & In-Laws', margin, y);
    y += 6;
    application.parentsInLaws?.forEach((parent, index) => {
      checkPage();
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text(`Parent ${index + 1} Name: `, margin, y);
      doc.setFont("helvetica", "normal");
      doc.text(parent.name || 'N/A', margin + 60, y);
      y += 5;
      doc.setFont("helvetica", "bold");
      doc.text('Gender: ', margin, y);
      doc.setFont("helvetica", "normal");
      doc.text(parent.gender || 'N/A', margin + 60, y);
      y += 5;
      doc.setFont("helvetica", "bold");
      doc.text('Date of Birth: ', margin, y);
      doc.setFont("helvetica", "normal");
      doc.text(formatDate(parent.dob) || 'N/A', margin + 60, y);
      y += 5;
      doc.setFont("helvetica", "bold");
      doc.text('Cover Type: ', margin, y);
      doc.setFont("helvetica", "normal");
      doc.text(parent.coverType || 'N/A', margin + 60, y);
      y += 5;
    });
    doc.setFont("helvetica", "bold");
    doc.text('Parents Interests: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.parentsInterests?.join(', ') || 'N/A', margin + 80, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Extended Family/Relatives', margin, y);
    y += 6;
    application.extendedFamily?.forEach((ext, index) => {
      checkPage();
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text(`Extended ${index + 1} Name: `, margin, y);
      doc.setFont("helvetica", "normal");
      doc.text(ext.name || 'N/A', margin + 60, y);
      y += 5;
      doc.setFont("helvetica", "bold");
      doc.text('Gender: ', margin, y);
      doc.setFont("helvetica", "normal");
      doc.text(ext.gender || 'N/A', margin + 60, y);
      y += 5;
      doc.setFont("helvetica", "bold");
      doc.text('Date of Birth: ', margin, y);
      doc.setFont("helvetica", "normal");
      doc.text(formatDate(ext.dob) || 'N/A', margin + 60, y);
      y += 5;
      doc.setFont("helvetica", "bold");
      doc.text('Cover Type: ', margin, y);
      doc.setFont("helvetica", "normal");
      doc.text(ext.coverType || 'N/A', margin + 60, y);
      y += 5;
    });
    doc.setFont("helvetica", "bold");
    doc.text('External Family Motivations: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.externalFamilyMotivations?.join(', ') || 'N/A', margin + 80, y);
    y += 10;
    checkPage();

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text('Section 8: Beneficiary Details', margin, y);
    y += 6;
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text('Title & Full Names: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.beneficiaryTitleFullNames || 'N/A', margin + 60, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('ID Number: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.beneficiaryIdNumber || 'N/A', margin + 60, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Nationality: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.beneficiaryNationality || 'N/A', margin + 60, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Relationship: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.beneficiaryRelationship || 'N/A', margin + 60, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Home Location/Village name: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.beneficiaryHomeLocationVillage || 'N/A', margin + 60, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Contact Number Calls: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.beneficiaryContactCalls || 'N/A', margin + 60, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Contact Number Text: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.beneficiaryContactText || 'N/A', margin + 60, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Email Address: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.beneficiaryEmailAddress || 'N/A', margin + 60, y);
    y += 10;
    checkPage();

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text('Section 8: Membership Fee & Payment Details', margin, y);
    y += 6;
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text('Main Subscription Fee Quantity: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(String(application.mainSubscriptionFeeQty) || 'N/A', margin + 90, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Membership Card Quantity: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(String(application.membershipCardQty) || 'N/A', margin + 90, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Funeral Main Spouse Quantity: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(String(application.funeralMainSpouseQty) || 'N/A', margin + 90, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Funeral Children Quantity: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(String(application.funeralChildrenQty) || 'N/A', margin + 90, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Funeral Extended 55 Qty: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(String(application.funeralExtended55Qty) || 'N/A', margin + 90, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Funeral Extended 90 Qty: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(String(application.funeralExtended90Qty) || 'N/A', margin + 90, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Funeral Extended 110 Qty: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(String(application.funeralExtended110Qty) || 'N/A', margin + 90, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Subscription Frequency: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.subscriptionFrequency || 'N/A', margin + 90, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Agree to Pay: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.agreeToPay ? 'Yes' : 'No', margin + 90, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Payment Method: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.paymentMethod || 'N/A', margin + 90, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('M-Pesa Number: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.mpesaNumber || 'N/A', margin + 90, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('EcoCash Number: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.ecocashNumber || 'N/A', margin + 90, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Account Holder: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.accountHolder || 'N/A', margin + 90, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Bank Name: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.bankName || 'N/A', margin + 90, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Account No.: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.accountNo || 'N/A', margin + 90, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Branch Code: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.branchCode || 'N/A', margin + 90, y);
    y += 10;
    checkPage();

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text('Declaration & Submission', margin, y);
    y += 6;
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text('Authorized Signature: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.authorizedSignature || 'N/A', margin + 60, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Date: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(formatDate(application.date) || 'N/A', margin + 60, y);
    y += 10;
    checkPage();

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text('Member (Applicant)', margin, y);
    y += 6;
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text('Title & Full Names: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.memberTitleFullNames || 'N/A', margin + 60, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Date: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(formatDate(application.memberDate) || 'N/A', margin + 60, y);
    y += 10;
    checkPage();

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text('FMLC Representative', margin, y);
    y += 6;
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text('Title & Full Names: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.fmlcRepTitleFullNames || 'N/A', margin + 60, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Position: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.fmlcRepPosition || 'N/A', margin + 60, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Date: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(formatDate(application.fmlcRepDate) || 'N/A', margin + 60, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Remarks: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.fmlcRepRemarks || 'N/A', margin + 60, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Signature: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.fmlcRepSignature || 'N/A', margin + 60, y);
    y += 10;
    checkPage();

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text('FMLC Approver', margin, y);
    y += 6;
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text('Title & Full Names: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.fmlcApprTitleFullNames || 'N/A', margin + 60, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Position: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.fmlcApprPosition || 'N/A', margin + 60, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Date: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(formatDate(application.fmlcApprDate) || 'N/A', margin + 60, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Remarks: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.fmlcApprRemarks || 'N/A', margin + 60, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text('Signature: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.fmlcApprSignature || 'N/A', margin + 60, y);
    y += 10;
    checkPage();

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text('Other Comments: ', margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(application.otherComments || 'N/A', margin + 60, y);
    y += 10;
    checkPage();

    addFooter();

    return doc;
  };

  const downloadApplication = (application) => {
    const doc = generateMembershipPDF(application);
    doc.save(`membership-application-${application.fullNames || 'application'}.pdf`);
  };

  const exportToCSV = () => {
    const headers = ['Name', 'Email', 'Contact', 'Approval Date'];
    const csvData = filteredApplications.map(app => [
      app.fullNames,
      app.personalEmailAddress,
      app.contactNumbersCallsMain,
      app.coordinatorReviewedAt ? new Date(app.coordinatorReviewedAt.seconds * 1000).toLocaleDateString() : 'N/A'
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field || 'N/A'}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'member-approved-applications.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div className="loading">Loading member applications...</div>;
  }

  return (
    <div className="member-applications">
      {showTitle && <h3>Approved Member Applications ({filteredApplications.length})</h3>}
      
      <div className="dashboard-controls">
        <div className="search-control">
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="export-buttons">
          <button onClick={exportToCSV} className="btn-primary">
            Export to CSV
          </button>
        </div>
      </div>

      <div className="table-container">
        <table className="dashboard-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Contact</th>
              <th>Approved Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredApplications.map(application => (
              <tr key={`member-${application.id}`}>
                <td>{application.fullNames}</td>
                <td>{application.personalEmailAddress}</td>
                <td>{application.contactNumbersCallsMain}</td>
                <td>
                  {application.coordinatorReviewedAt ?
                    new Date(application.coordinatorReviewedAt.seconds * 1000).toLocaleDateString() :
                    'N/A'
                  }
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="btn-primary"
                      onClick={() => viewApplicationDetails(application)}
                    >
                      View
                    </button>
                    <button
                      className="btn-success"
                      onClick={() => downloadApplication(application)}
                    >
                      Download PDF
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredApplications.length === 0 && (
          <p className="no-data">No approved member applications found.</p>
        )}
      </div>

      {showModal && selectedApplication && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Member Application Details</h3>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>
            <div className="modal-body" style={{ overflowY: 'auto', maxHeight: '80vh' }}>
              <div className="application-details">
                <h4>Application Type</h4>
                <div className="detail-row">
                  <span className="detail-label">Application Type:</span>
                  <span className="detail-value">{selectedApplication.applicationType || 'N/A'}</span>
                </div>
                
                <h4>Membership Plan</h4>
                <div className="detail-row">
                  <span className="detail-label">Membership Plan:</span>
                  <span className="detail-value">{selectedApplication.membershipPlan || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Member Card Fee:</span>
                  <span className="detail-value">{selectedApplication.memberCardFee ? 'Yes' : 'No'}</span>
                </div>

                <h4>Section 1: Personal Details</h4>
                <div className="detail-row">
                  <span className="detail-label">Full Names:</span>
                  <span className="detail-value">{selectedApplication.fullNames || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Other Names:</span>
                  <span className="detail-value">{selectedApplication.otherNames || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Date of Birth:</span>
                  <span className="detail-value">{formatDate(selectedApplication.dateOfBirth) || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Identity Document Number:</span>
                  <span className="detail-value">{selectedApplication.identityDocumentNumber || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Gender:</span>
                  <span className="detail-value">{selectedApplication.gender || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Nationality:</span>
                  <span className="detail-value">{selectedApplication.nationality || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Current Marital Status:</span>
                  <span className="detail-value">{selectedApplication.currentMaritalStatus || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Full Names of Spouse:</span>
                  <span className="detail-value">{selectedApplication.fullNamesSpouse || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Previous Marital Status:</span>
                  <span className="detail-value">{selectedApplication.previousMaritalStatus?.join(', ') || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Full Names of Previous Spouse:</span>
                  <span className="detail-value">{selectedApplication.fullNamesPreviousSpouse || 'N/A'}</span>
                </div>

                <h4>Section 2: Contacts & Residence</h4>
                <div className="detail-row">
                  <span className="detail-label">Contact Numbers for Calls (Main):</span>
                  <span className="detail-value">{selectedApplication.contactNumbersCallsMain || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Contact Numbers for Calls (Other):</span>
                  <span className="detail-value">{selectedApplication.contactNumbersCallsOther || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Contact Numbers for WhatsApp/SMS (Main):</span>
                  <span className="detail-value">{selectedApplication.contactNumbersWhatsappMain || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Contact Numbers for WhatsApp/SMS (Other):</span>
                  <span className="detail-value">{selectedApplication.contactNumbersWhatsappOther || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Personal Email Address:</span>
                  <span className="detail-value">{selectedApplication.personalEmailAddress || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Home Location/Village name:</span>
                  <span className="detail-value">{selectedApplication.homeLocationVillage || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Street full description:</span>
                  <span className="detail-value">{selectedApplication.streetFullDescription || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Name of homeowner / Chief:</span>
                  <span className="detail-value">{selectedApplication.nameHomeownerChief || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Social Media Channel & Names (Link):</span>
                  <span className="detail-value">{selectedApplication.socialMediaChannelNames || 'N/A'}</span>
                </div>

                <h4>Section 3: Next of Kin Details</h4>
                <div className="detail-row">
                  <span className="detail-label">Title & Full Names:</span>
                  <span className="detail-value">{selectedApplication.nextKinTitleFullNames || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Relationship:</span>
                  <span className="detail-value">{selectedApplication.nextKinRelationship || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Home Location/Village name:</span>
                  <span className="detail-value">{selectedApplication.nextKinHomeLocationVillage || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Contact Number Calls:</span>
                  <span className="detail-value">{selectedApplication.nextKinContactCalls || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Contact Number Text:</span>
                  <span className="detail-value">{selectedApplication.nextKinContactText || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Email Address:</span>
                  <span className="detail-value">{selectedApplication.nextKinEmailAddress || 'N/A'}</span>
                </div>

                <h4>Section 4: Career & Employment</h4>
                <div className="detail-row">
                  <span className="detail-label">Current Employer / Entity name:</span>
                  <span className="detail-value">{selectedApplication.currentEmployerName || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Current Start Date - End Date:</span>
                  <span className="detail-value">{selectedApplication.currentStartDateEndDate || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Current Department:</span>
                  <span className="detail-value">{selectedApplication.currentDepartment || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Current Position:</span>
                  <span className="detail-value">{selectedApplication.currentPosition || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Current Employment Number:</span>
                  <span className="detail-value">{selectedApplication.currentEmploymentNumber || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Current Contact Number Main:</span>
                  <span className="detail-value">{selectedApplication.currentContactMain || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Current Place / location:</span>
                  <span className="detail-value">{selectedApplication.currentPlaceLocation || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Current Industry or sector:</span>
                  <span className="detail-value">{selectedApplication.currentIndustrySector || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Immediate Past Employer / Entity name:</span>
                  <span className="detail-value">{selectedApplication.immediatePastEmployerName || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Immediate Past Start Date - End Date:</span>
                  <span className="detail-value">{selectedApplication.immediatePastStartDateEndDate || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Immediate Past Department:</span>
                  <span className="detail-value">{selectedApplication.immediatePastDepartment || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Immediate Past Position:</span>
                  <span className="detail-value">{selectedApplication.immediatePastPosition || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Immediate Past Employment Number:</span>
                  <span className="detail-value">{selectedApplication.immediatePastEmploymentNumber || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Immediate Past Contact Number Main:</span>
                  <span className="detail-value">{selectedApplication.immediatePastContactMain || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Immediate Past Place / location:</span>
                  <span className="detail-value">{selectedApplication.immediatePastPlaceLocation || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Immediate Past Industry or sector:</span>
                  <span className="detail-value">{selectedApplication.immediatePastIndustrySector || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Highest Qualification:</span>
                  <span className="detail-value">{selectedApplication.highestQualification || 'N/A'}</span>
                </div>

                <h4>Section 7: Funeral Cover & Benefits</h4>
                <div className="detail-row">
                  <span className="detail-label">Children:</span>
                  {selectedApplication.children && selectedApplication.children.length > 0 ? (
                    selectedApplication.children.map((child, index) => (
                      <div key={index} style={{ marginLeft: '20px' }}>
                        <span>Child {index + 1} Name: {child.name || 'N/A'}</span><br />
                        <span>Gender: {child.gender || 'N/A'}</span><br />
                        <span>Date of Birth: {formatDate(child.dob) || 'N/A'}</span><br />
                        <span>Issue Card: {child.issueCard || 'N/A'}</span><br />
                      </div>
                    ))
                  ) : (
                    <span className="detail-value">N/A</span>
                  )}
                </div>
                <div className="detail-row">
                  <span className="detail-label">Children's Personalities:</span>
                  <span className="detail-value">{selectedApplication.childrenPersonalities?.join(', ') || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Add Parents/In-Laws/Extended:</span>
                  <span className="detail-value">{selectedApplication.addParentsInLawsExtended?.join(', ') || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Parents & In-Laws:</span>
                  {selectedApplication.parentsInLaws && selectedApplication.parentsInLaws.length > 0 ? (
                    selectedApplication.parentsInLaws.map((parent, index) => (
                      <div key={index} style={{ marginLeft: '20px' }}>
                        <span>Parent {index + 1} Name: {parent.name || 'N/A'}</span><br />
                        <span>Gender: {parent.gender || 'N/A'}</span><br />
                        <span>Date of Birth: {formatDate(parent.dob) || 'N/A'}</span><br />
                        <span>Cover Type: {parent.coverType || 'N/A'}</span><br />
                      </div>
                    ))
                  ) : (
                    <span className="detail-value">N/A</span>
                  )}
                </div>
                <div className="detail-row">
                  <span className="detail-label">Parents Interests:</span>
                  <span className="detail-value">{selectedApplication.parentsInterests?.join(', ') || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Extended Family/Relatives:</span>
                  {selectedApplication.extendedFamily && selectedApplication.extendedFamily.length > 0 ? (
                    selectedApplication.extendedFamily.map((ext, index) => (
                      <div key={index} style={{ marginLeft: '20px' }}>
                        <span>Extended {index + 1} Name: {ext.name || 'N/A'}</span><br />
                        <span>Gender: {ext.gender || 'N/A'}</span><br />
                        <span>Date of Birth: {formatDate(ext.dob) || 'N/A'}</span><br />
                        <span>Cover Type: {ext.coverType || 'N/A'}</span><br />
                      </div>
                    ))
                  ) : (
                    <span className="detail-value">N/A</span>
                  )}
                </div>
                <div className="detail-row">
                  <span className="detail-label">External Family Motivations:</span>
                  <span className="detail-value">{selectedApplication.externalFamilyMotivations?.join(', ') || 'N/A'}</span>
                </div>

                <h4>Section 8: Beneficiary Details</h4>
                <div className="detail-row">
                  <span className="detail-label">Title & Full Names:</span>
                  <span className="detail-value">{selectedApplication.beneficiaryTitleFullNames || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">ID Number:</span>
                  <span className="detail-value">{selectedApplication.beneficiaryIdNumber || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Nationality:</span>
                  <span className="detail-value">{selectedApplication.beneficiaryNationality || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Relationship:</span>
                  <span className="detail-value">{selectedApplication.beneficiaryRelationship || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Home Location/Village name:</span>
                  <span className="detail-value">{selectedApplication.beneficiaryHomeLocationVillage || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Contact Number Calls:</span>
                  <span className="detail-value">{selectedApplication.beneficiaryContactCalls || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Contact Number Text:</span>
                  <span className="detail-value">{selectedApplication.beneficiaryContactText || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Email Address:</span>
                  <span className="detail-value">{selectedApplication.beneficiaryEmailAddress || 'N/A'}</span>
                </div>

                <h4>Section 8: Membership Fee & Payment Details</h4>
                <div className="detail-row">
                  <span className="detail-label">Main Subscription Fee Quantity:</span>
                  <span className="detail-value">{selectedApplication.mainSubscriptionFeeQty || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Membership Card Quantity:</span>
                  <span className="detail-value">{selectedApplication.membershipCardQty || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Additional FMLC Membership:</span>
                  <span className="detail-value">{selectedApplication.additionalMembershipQty || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Funeral Cover for Main member & spouse:</span>
                  <span className="detail-value">{selectedApplication.funeralMainSpouseQty || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Funeral Cover for Children (up to 6):</span>
                  <span className="detail-value">{selectedApplication.funeralChildrenQty || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Funeral Cover for Parent 1:</span>
                  <span className="detail-value">{selectedApplication.funeralParent1Qty || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Funeral Cover for Parent 2:</span>
                  <span className="detail-value">{selectedApplication.funeralParent2Qty || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Funeral Cover for Parent in-law 1:</span>
                  <span className="detail-value">{selectedApplication.funeralInLaw1Qty || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Funeral Cover for Parent in-law 2:</span>
                  <span className="detail-value">{selectedApplication.funeralInLaw2Qty || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Funeral Cover for Extended & Relatives M55.00:</span>
                  <span className="detail-value">{selectedApplication.funeralExtended55Qty || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Funeral Cover for Extended & Relatives M90.00:</span>
                  <span className="detail-value">{selectedApplication.funeralExtended90Qty || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Funeral Cover for Extended & Relatives M110.00:</span>
                  <span className="detail-value">{selectedApplication.funeralExtended110Qty || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Subscription Frequency:</span>
                  <span className="detail-value">{selectedApplication.subscriptionFrequency || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Agree to Pay:</span>
                  <span className="detail-value">{selectedApplication.agreeToPay ? 'Yes' : 'No'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Payment Method:</span>
                  <span className="detail-value">{selectedApplication.paymentMethod || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">M-Pesa Number:</span>
                  <span className="detail-value">{selectedApplication.mpesaNumber || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">EcoCash Number:</span>
                  <span className="detail-value">{selectedApplication.ecocashNumber || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Account Holder:</span>
                  <span className="detail-value">{selectedApplication.accountHolder || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Bank Name:</span>
                  <span className="detail-value">{selectedApplication.bankName || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Account No.:</span>
                  <span className="detail-value">{selectedApplication.accountNo || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Branch Code:</span>
                  <span className="detail-value">{selectedApplication.branchCode || 'N/A'}</span>
                </div>

                <h4>Declaration & Submission</h4>
                <div className="detail-row">
                  <span className="detail-label">Authorized Signature:</span>
                  <span className="detail-value">{selectedApplication.declarationSignature || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Date:</span>
                  <span className="detail-value">{formatDate(selectedApplication.declarationDate) || 'N/A'}</span>
                </div>

                <h4>Member (Applicant)</h4>
                <div className="detail-row">
                  <span className="detail-label">Title & Full Names:</span>
                  <span className="detail-value">{selectedApplication.memberTitleFullNames || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Date:</span>
                  <span className="detail-value">{formatDate(selectedApplication.memberDate) || 'N/A'}</span>
                </div>

                <h4>FMLC Representative</h4>
                <div className="detail-row">
                  <span className="detail-label">Title & Full Names:</span>
                  <span className="detail-value">{selectedApplication.fmlcRepTitleFullNames || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Position:</span>
                  <span className="detail-value">{selectedApplication.fmlcRepPosition || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Date:</span>
                  <span className="detail-value">{formatDate(selectedApplication.fmlcRepDate) || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Remarks:</span>
                  <span className="detail-value">{selectedApplication.fmlcRepRemarks || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Signature:</span>
                  <span className="detail-value">{selectedApplication.fmlcRepSignature || 'N/A'}</span>
                </div>

                {editApprover ? (
                  <div className="edit-approver-form">
                    <h4>Edit FMLC Approver Details</h4>
                    <div className="detail-row">
                      <span className="detail-label">Title & Full Names:</span>
                      <input
                        type="text"
                        name="fmlcApprTitleFullNames"
                        value={approverData.fmlcApprTitleFullNames}
                        onChange={handleApproverChange}
                        className="detail-input"
                      />
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Position:</span>
                      <input
                        type="text"
                        name="fmlcApprPosition"
                        value={approverData.fmlcApprPosition}
                        onChange={handleApproverChange}
                        className="detail-input"
                      />
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Date:</span>
                      <input
                        type="date"
                        name="fmlcApprDate"
                        value={approverData.fmlcApprDate}
                        onChange={handleApproverChange}
                        className="detail-input"
                      />
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Signature:</span>
                      <input
                        type="text"
                        name="fmlcApprSignature"
                        value={approverData.fmlcApprSignature}
                        onChange={handleApproverChange}
                        className="detail-input"
                      />
                    </div>
                    <div className="form-actions">
                      <button className="btn-primary" onClick={saveApproverChanges}>
                        Save Changes
                      </button>
                      <button className="btn-secondary" onClick={() => setEditApprover(false)}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="approver-section">
                    <h4>FMLC Approver</h4>
                    <div className="detail-row">
                      <span className="detail-label">Title & Full Names:</span>
                      <span className="detail-value">{selectedApplication.fmlcApprTitleFullNames || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Position:</span>
                      <span className="detail-value">{selectedApplication.fmlcApprPosition || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Date:</span>
                      <span className="detail-value">{formatDate(selectedApplication.fmlcApprDate) || 'N/A'}</span>
                    </div>                    <div className="detail-row">
                      <span className="detail-label">Remarks:</span>
                      <span className="detail-value">{selectedApplication.fmlcApprRemarks || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Signature:</span>
                      <span className="detail-value">{selectedApplication.fmlcApprSignature || 'N/A'}</span>
                    </div>
                    <button
                      className="btn-primary"
                      onClick={() => setEditApprover(true)}
                      style={{ marginTop: '10px' }}
                    >
                      Edit Approver Details
                    </button>
                  </div>
                )}
                <h4>Other Comments</h4>
                <div className="detail-row">
                  <span className="detail-label">Comments:</span>
                  <span className="detail-value">{selectedApplication.otherComments || 'N/A'}</span>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={closeModal}>
                Close
              </button>
              <button
                className="btn-success"
                onClick={() => downloadApplication(selectedApplication)}
              >
                Download PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemberApplications;